/**
 * Tribunales Tributarios y Aduaneros (TTA), derivados del RIT sin gasto.
 *
 * El RIT de una sentencia (`documents.numero`, p. ej. `GR-16-00038-2022`) lleva en su segundo tramo el código del
 * tribunal. Medido en la BD el 15-10 sobre las 2.739 sentencias: el código coincide en el 100 % con
 * `legislacion[0].id_tribunal` (metadato de la OJV) y con la firma del juez al pie de la sentencia («Juez … del
 * Cuarto Tribunal Tributario y Aduanero de la Región Metropolitana»), sin contradicciones. El código 05 no existe.
 * Orden: de norte a sur, con los cuatro de la Región Metropolitana después de Valparaíso.
 */

export interface TribunalTTA {
  codigo: string;
  /** Nombre corto para filtros y filas. */
  nombre: string;
}

export const TRIBUNALES_TTA: TribunalTTA[] = [
  { codigo: '01', nombre: 'TTA Arica y Parinacota' },
  { codigo: '02', nombre: 'TTA Tarapacá' },
  { codigo: '03', nombre: 'TTA Antofagasta' },
  { codigo: '04', nombre: 'TTA Atacama' },
  { codigo: '06', nombre: 'TTA Coquimbo' },
  { codigo: '14', nombre: 'TTA Valparaíso' },
  { codigo: '15', nombre: '1.er TTA Región Metropolitana' },
  { codigo: '16', nombre: '2.º TTA Región Metropolitana' },
  { codigo: '17', nombre: '3.er TTA Región Metropolitana' },
  { codigo: '18', nombre: '4.º TTA Región Metropolitana' },
  { codigo: '19', nombre: "TTA O'Higgins" },
  { codigo: '07', nombre: 'TTA Maule' },
  { codigo: '10', nombre: 'TTA Ñuble y Biobío' },
  { codigo: '08', nombre: 'TTA La Araucanía' },
  { codigo: '11', nombre: 'TTA Los Ríos' },
  { codigo: '12', nombre: 'TTA Los Lagos' },
  { codigo: '13', nombre: 'TTA Aysén' },
  { codigo: '09', nombre: 'TTA Magallanes' },
];

const POR_CODIGO = new Map(TRIBUNALES_TTA.map(t => [t.codigo, t]));

/** Tribunal de un RIT (`GR-16-00038-2022` → 2.º TTA RM); undefined si el formato no calza o el código no existe. */
export function tribunalDeRit(numero: string | null | undefined): TribunalTTA | undefined {
  const m = /^[A-Z]{2,3}\s*-\s*(\d{2})\s*-\s*\d{5}\s*-\s*\d{4}$/.exec((numero || '').trim().toUpperCase());
  return m ? POR_CODIGO.get(m[1]) : undefined;
}

/** true si `codigo` es un código de tribunal conocido (lo que acepta el backend en `?tribunal=`). */
export const esCodigoTribunal = (codigo: string | null | undefined): boolean => !!codigo && POR_CODIGO.has(codigo);

/**
 * Pestañas por procedimiento de las sentencias del TTA (DESIGN.md §11.3, maqueta m3). La clave es la del parámetro
 * `procedimiento` del backend (rama claude/fervent-leakey-333e9c), que agrupa `legislacion[0].procedimiento` de la OJV.
 * Los 6 casos de «Secreto bancario» y «Elusión» no tienen pestaña: salen solo en «Todas».
 */
export const PROCEDIMIENTOS_TTA = [
  { clave: 'reclamacion', texto: 'Reclamaciones' },
  { clave: 'sancion', texto: 'Sanciones' },
  { clave: 'avaluo', texto: 'Avalúos' },
  { clave: 'derechos', texto: 'Derechos' },
  { clave: 'multa', texto: 'Multas' },
] as const;

export const esProcedimientoTta = (v: string) => PROCEDIMIENTOS_TTA.some(p => p.clave === v);
